import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    BigModal,
    FlexDiv,
    SearchInput,
    TextTiny,
} from '@awarego-ui/components';
import { useStores } from '../../../../hooks/use-stores';
import TableClient from '../../../../components/table/TableClient';
import ThemedSpinner from '../../../../components/themed/ThemedSpinner';
import ErrorMessage from '../../../../components/errormessage';

function ReminderUsersModal({
    isOpen,
    reminder,
    companyId,
    assessmentId,
    onClose,
}) {
    const { assessmentStore } = useStores();
    const [searchValue, setSearchValue] = useState('');
    const rowsPerPage = 10;

    const { reminderUsers, loadingReminderUsers, reminderUsersError } =
        assessmentStore;

    useEffect(() => {
        if (!isOpen || !reminder?.notificationReasonId) return;
        setSearchValue('');
        assessmentStore.loadReminderUsers(
            companyId,
            assessmentId,
            reminder.notificationReasonId
        );
    }, [
        isOpen,
        reminder?.notificationReasonId,
        companyId,
        assessmentId,
        assessmentStore,
    ]);

    const handleClose = () => {
        assessmentStore.clearReminderUsers();
        setSearchValue('');
        onClose();
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: (x) => x.value || x.row.original.email,
            },
            {
                Header: 'Email',
                accessor: 'email',
            },
        ],
        []
    );

    const users = reminderUsers.slice();
    const headingCount =
        reminder?.notificationsCount || users.length
            ? ` (${reminder?.notificationsCount || users.length})`
            : '';

    const modalContent = (
        <FlexDiv column gap={16}>
            {reminderUsersError && <ErrorMessage error={reminderUsersError} />}
            {loadingReminderUsers ? (
                <ThemedSpinner />
            ) : (
                <>
                    <FlexDiv justifyEnd>
                        <SearchInput
                            label=""
                            placeholder="Search"
                            searchValue={searchValue}
                            onChange={(e) =>
                                setSearchValue(e.target.value || '')
                            }
                            onClear={() => setSearchValue('')}
                        />
                    </FlexDiv>
                    <TableClient
                        columns={columns}
                        data={users}
                        initialPageSize={rowsPerPage}
                        defaultSortBy="name"
                        defaultSortDescent={false}
                        hidePagination={users.length <= rowsPerPage}
                        filterValue={searchValue}
                        getRowId={(row) => row.email}
                        NoDataComponent={
                            <TextTiny lighter>
                                No employees were found for this reminder.
                            </TextTiny>
                        }
                    />
                </>
            )}
        </FlexDiv>
    );

    return (
        <BigModal
            isOpen={isOpen}
            onRequestClose={handleClose}
            heading={`Affected employees${headingCount}`}
            extraHeaderContent={
                reminder ? (
                    <TextTiny lighter>
                        {[reminder.sendTo, reminder.initiatedBy]
                            .filter(Boolean)
                            .join(' · ')}
                    </TextTiny>
                ) : null
            }
            width={720}
            modalContent={modalContent}
        />
    );
}

export default observer(ReminderUsersModal);
